import React, { useState } from "react";
import axios from "axios";
import { MessageCircle, Phone, Mail, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useCatalog } from "../contexts/CatalogContext";
import { API } from "../lib/api";

export default function Contact() {
  const { CONTACT, CITIES } = useCatalog();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please enter your name and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please try WhatsApp or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="text-xs font-semibold text-[#FF6B2B] uppercase tracking-widest">Contact</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">We're here to help</h1>
        <p className="mt-4 text-slate-600">WhatsApp is fastest. Or drop us a message — we reply within 24 hours.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <a
          href={CONTACT.whatsappLink}
          target="_blank"
          rel="noreferrer"
          data-testid="contact-whatsapp"
          className="bg-[#22C55E] text-white rounded-2xl p-7 hover:scale-[1.02] transition-transform"
        >
          <MessageCircle className="w-8 h-8" />
          <h3 className="font-display font-bold text-lg mt-4">WhatsApp Us</h3>
          <p className="text-sm text-green-50 mt-1">Fastest response — usually under 5 min</p>
          <span className="inline-block mt-4 text-sm font-semibold underline">Open WhatsApp →</span>
        </a>

        <a
          href={CONTACT.phoneLink}
          data-testid="contact-call"
          className="bg-[#1A3C6E] text-white rounded-2xl p-7 hover:scale-[1.02] transition-transform"
        >
          <Phone className="w-8 h-8" />
          <h3 className="font-display font-bold text-lg mt-4">Call Now</h3>
          <p className="text-sm text-slate-300 mt-1">{CONTACT.phone}</p>
          <span className="inline-block mt-4 text-sm font-semibold underline">Tap to call →</span>
        </a>

        <a
          href={CONTACT.emailLink}
          data-testid="contact-email"
          className="bg-white border border-slate-200 rounded-2xl p-7 hover:scale-[1.02] transition-transform"
        >
          <Mail className="w-8 h-8 text-[#FF6B2B]" />
          <h3 className="font-display font-bold text-lg mt-4 text-slate-900">Email Us</h3>
          <p className="text-sm text-slate-500 mt-1">{CONTACT.email}</p>
          <span className="inline-block mt-4 text-sm font-semibold text-[#FF6B2B]">Send email →</span>
        </a>
      </div>

      {/* Form + Coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-7 card-shadow" data-testid="contact-form">
          <h2 className="font-display font-bold text-xl text-slate-900">Send us a message</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Full name *</label>
              <input
                data-testid="contact-name"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Phone</label>
              <input
                data-testid="contact-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input
                type="email"
                data-testid="contact-email-field"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-slate-700">Message *</label>
              <textarea
                data-testid="contact-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/10 outline-none"
              />
            </div>
          </div>
          <button
            data-testid="contact-submit"
            disabled={submitting}
            type="submit"
            className="mt-5 bg-[#FF6B2B] hover:bg-[#E65A20] text-white font-semibold px-6 py-3 rounded-xl transition-all inline-flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Send message"} <Send className="w-4 h-4" />
          </button>
        </form>

        <aside className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="coverage-aside">
          <h3 className="font-display font-bold text-lg text-slate-900">Coverage</h3>
          <p className="text-sm text-slate-600 mt-1">Cities we service today.</p>
          <ul className="mt-5 space-y-2">
                  {CITIES.map((c) => (
              <li key={c.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <span className="flex items-center gap-2 font-semibold text-slate-800">
                  <MapPin className="w-4 h-4 text-[#1A3C6E]" /> {c.name}
                </span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.status === "live" ? "bg-[#22C55E] text-white" : "bg-slate-200 text-slate-600"}`}>
                  {c.status === "live" ? "Live" : "Coming soon"}
                </span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
